import { useEffect, type ReactNode } from 'react'
import Column from '@app/components/layout/Column'
import { match } from 'ts-pattern'
import { useQuery } from '@based/sdk/react'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { regionsTabs } from '@app/pages/discover/regions/constants'
import { Initiatives } from '../regionTabs/tabContent/Initiatives'
import { Thread } from '@app/components/blocks/thread/Thread'
import { Locations } from '../regionTabs/tabContent/Locations'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { RegionalServiceProvidersGrid } from '../regionTabs/tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'
import { RegionalActueelTab } from './RegionalActueelTab'
import { RegionalMunicipalitiesGrid } from './RegionalMunicipalitiesGrid'
import { useRegionalLayoutTabAvailability } from './useRegionalLayoutTabAvailability'
import type { RegionsMunicipalitiesFeatures } from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'

export const RegionalPageTabContent = ({
  region,
  regionSlug,
  tab,
}: {
  region: RegionViewModel
  regionSlug: string
  tab: RegionsMunicipalitiesFeatures
}) => {
  const { isTabAvailable } = useRegionalLayoutTabAvailability(region)

  useEffect(() => {
    if (tab === regionsTabs.overview) {
      return
    }
    if (!isTabAvailable(tab)) {
      navigate(pages.regionPageNew(regionSlug))
    }
  }, [tab, isTabAvailable, regionSlug])

  const expertsFeatureEnabled = isFeatureEnabled(
    region.featureToggles,
    regionsTabs.experts,
  )
  const regionalExpertsListQuery = useQuery(
    tab === regionsTabs.experts && expertsFeatureEnabled
      ? 'list-regional-service-providers'
      : null,
    { regionId: region.id },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  if (tab === regionsTabs.overview) {
    return null
  }

  return (
    <Column alignItems="stretch" gap="2rem">
      {match(tab)
        .with(regionsTabs.current, () => (
          <RegionalActueelTab
            key="current"
            region={region}
            regionSlug={regionSlug}
          />
        ))
        .with(regionsTabs.discussion, () => (
          <Thread key="thread" threadId={region.thread?.id} />
        ))
        .with(regionsTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={region} />
        ))
        .with(regionsTabs.municipalities, () => (
          <RegionalMunicipalitiesGrid
            key="municipalities"
            regionId={region.id}
          />
        ))
        .with(regionsTabs.locations, () => (
          <Locations key="locations" entity={region} />
        ))
        .with(regionsTabs.knowledgeHub, () => (
          <KnowledgeHubModulesSection
            key="knowledgeHub"
            modules={region.knowledgeHubModules ?? []}
            regionSlug={regionSlug}
          />
        ))
        .with(regionsTabs.experts, () => (
          <RegionalServiceProvidersGrid
            key="experts"
            serviceProviders={regionalExperts}
          />
        ))
        .with(regionsTabs.developmentAreas, (): ReactNode => null)
        .with(regionsTabs.stepByStep, (): ReactNode => null)
        .with(regionsTabs.subsidies, (): ReactNode => null)
        .exhaustive()}
    </Column>
  )
}
